
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout, { NavItem } from './components/Layout';
import ProductGrid from './components/POS/ProductGrid';
import Cart from './components/POS/Cart';
import PaymentModal from './components/POS/PaymentModal';
import ReceiptModal from './components/POS/ReceiptModal';
import DistributionReceiptModal from './components/Inventory/DistributionReceiptModal';
import InventoryList from './components/Inventory/InventoryList';
import InventoryOut from './components/Inventory/InventoryOut';
import WarehouseTransferWizard from './components/Inventory/WarehouseTransferWizard';
import WarehouseTransferView from './components/Inventory/WarehouseTransferView';
import StockReport from './components/Reports/StockReport';
import StockTrackView from './components/Inventory/StockTrackView';
import BarcodeDirectory from './components/Inventory/BarcodeDirectory';
import CustomerList from './components/Customers/CustomerList';
import TransactionList from './components/Transactions/TransactionList';
import ReturnsView from './components/Returns/ReturnsView';
import ReturnReasonModal from './components/Returns/ReturnReasonModal';
import ExchangeView from './components/Exchange/ExchangeView';
import CollectionsView from './components/Settings/CollectionsView';
import CashierDashboard from './components/CashierManagement/CashierDashboard';
import ReportsDashboard from './components/Reports/ReportsDashboard';
import AIAssistant from './components/AIAssistant';
import Login from './components/Login';
import StartShift from './components/Shift/StartShift';
import EndShiftModal from './components/Shift/EndShiftModal';
import AuditLogsView from './components/Admin/AuditLogsView';
import BackupExportView from './components/Admin/BackupExportView';
import { useToast } from './contexts/ToastContext';
import {
  ViewState, Product, ProductSize, CartItem, Transaction, Customer,
  SessionInfo, ShiftRecord, CashierUser, UserRole, TransactionType,
  AuditLog, StockMovement, StockMovementType, ToastMessage, Collections,
  ReturnRecord, toBackendMovementType, BackendMovementType
} from './types';
import { getShiftDetails } from './utils';
import {
  LayoutDashboard, ShoppingCart, Archive, Users, Layers, TrendingUp,
  Truck, FileText, ClipboardList, Database, Barcode, History, RotateCcw, ArrowLeftRight
} from 'lucide-react';
import qz from "qz-tray";

// ─── helpers ──────────────────────────────────────────────────────────────────

/** Backend shiftType enum only has 'Day' | 'Night'. Map 'Morning' → 'Day'. */
function toBackendShiftType(shift: string): 'Day' | 'Night' {
  return shift === 'Night' ? 'Night' : 'Day';
}

/** Backend location enum: 'Store' | 'Warehouse'. */
function toBackendLocation(locationType: string): 'Store' | 'Warehouse' {
  return locationType === 'popup' ? 'Warehouse' : 'Store';
}

/** Transform a raw backend product row (flat) → frontend Product shape. */
function transformProduct(p: any, collectionsList: Collections[]): Product {
  const collectionName =
    collectionsList.find(c => c.id === p.collectionId)?.name ?? p.collectionId ?? '';
  return {
    _id: p.productId,
    id: p.productId,
    name: p.productName,
    collection: collectionName,
    collectionId: p.collectionId,
    price: parseFloat(p.price) || 0,
    image: p.image ||
      `https://placehold.co/400x400/e2e8f0/1e293b?text=${encodeURIComponent(p.productName)}`,
    stocks: (p.stocks || []).map((s: any) => ({
      sku: `${p.productId}-${s.size}`,
      size: s.size,
      stock: s.storeStock ?? 0,
      warehouseStock: s.warehouseStock ?? 0,
      productStockId: s.productStockId,
      stockId: s.stockId,
      barcode: s.barcode ?? undefined,
    })),
    createdAt: p.createdAt,
  };
}

/** Transform a raw backend customer row → frontend Customer shape. */
function transformCustomer(c: any): Customer {
  return {
    _id: c.customerId,
    id: c.customerId,
    name: c.customerName || '',
    phone: c.customerPhone || '',
    email: c.customerEmail || '',
    loyaltyPoints: 0,
    totalSpent: parseFloat(c.totalSpent) || 0,
  };
}

/** Transform a raw backend collection row → frontend Collections shape. */
function transformCollection(c: any): Collections {
  return {
    _id: c.collectionId,
    id: c.collectionId,
    name: c.collectionName,
  };
}

/**
 * Transform a flat backend transaction row → frontend Transaction.
 * Each backend row is one line-item; we reconstruct a CartItem if products are available.
 */
function transformTransaction(
  t: any,
  customersList: Customer[],
  productsList?: Product[]
): Transaction {
  const customer = t.customerId
    ? customersList.find(c => c._id === t.customerId)
    : undefined;

  // Reconstruct a single CartItem from productStockId if inventory is available
  let items: CartItem[] = [];
  if (productsList && t.productStockId) {
    for (const product of productsList) {
      const size = product.stocks.find(s => s.productStockId === t.productStockId);
      if (size) {
        items = [{ ...product, selectedSize: size, quantity: t.quantity ?? 1 }];
        break;
      }
    }
  }
    const DiscountPercentage= ((t.subtotal - t.total ) /t.subtotal ) *100

  return {
    id: t.transactionId,
    transactionId: t.transactionId,
    date: new Date(t.createdAt),
    type: t.type as TransactionType,
    items,
    subtotal: t.subtotal ?? 0,
    total: t.total ?? 0,
    paymentMethod: t.paymentMethod ?? 'N/A',
    amountPaid: t.total ?? 0,
    balance: 0,
    isPartial: false,
    customer,
    customerId: t.customerId,
    shift: t.shiftType ?? 'Day',
    shiftType: t.shiftType,
    businessDate: new Date(t.createdAt).toLocaleDateString('en-CA'),
    cashierName: t.cashierName ?? '',
    orderDiscount: DiscountPercentage ?? 0,
    transferReferenceNumber: t.transferReferenceNumber,
    isReturned: t.isReturned ?? false,
    isExchanged: t.isExchanged ?? false,
    productStockId: t.productStockId,
    quantity: t.quantity,
  };
}

/** Transform a raw backend user row → frontend CashierUser. */
function transformUser(u: any): CashierUser {
  return {
    _id: u.userId,
    id: u.userId,
    username: u.userName,
    name: u.userName,       // no separate display name in backend
    email: u.email || '',
    role: u.role as UserRole,
    phoneNumber: u.phoneNumber,
  };
}

/** Transform a raw backend stock_movement row → frontend StockMovement. */
function transformStockMovement(m: any): StockMovement {
  return {
    id: m.movementId,
    movementId: m.movementId,
    timestamp: m.createdAt,
    productId: m.productId,
    productName: '',         // not stored in movement table; filled by caller if needed
    size: m.productSize,     // this is actually the stockId UUID per schema
    stockId: m.productSize,
    type: m.movementType as any,
    movementType: m.movementType,
    location: 'Both',
    quantityChange: m.quantityChange,
    newStoreStock: m.updatedStoreStock,
    newWhStock: m.updatedWarehouseStock,
    updatedStoreStock: m.updatedStoreStock,
    updatedWarehouseStock: m.updatedWarehouseStock,
    performedBy: '',
    notes: m.referenceId ?? undefined,   // shared across all items in the same transaction
  };
}

// ─── App ──────────────────────────────────────────────────────────────────────

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<string>('');
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [currentUserRole, setCurrentUserRole] = useState<UserRole | null>(null);
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [currentView, setCurrentView] = useState<ViewState>(ViewState.POS);
  const [products, setProducts] = useState<Product[]>([]);
  const [users, setUsers] = useState<CashierUser[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [stockMovements, setStockMovements] = useState<StockMovement[]>([]);
  const [shiftHistory, setShiftHistory] = useState<ShiftRecord[]>([]);
  const { addToast } = useToast();
  const [collections, setCollections] = useState<Collections[]>([]);
  const [orderDiscount, setOrderDiscount] = useState<number>(0);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [showEndShiftModal, setShowEndShiftModal] = useState(false);
  const [lastTransaction, setLastTransaction] = useState<Transaction | null>(null);
  const [lastDistribution, setLastDistribution] = useState<Transaction | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [lowStockThreshold, setLowStockThreshold] = useState(30);
  const [pendingCheckout, setPendingCheckout] = useState<{
    amountPaid: number; balance: number; isPartial: boolean;
    cashReceived: number; changeAmount: number;
  } | null>(null);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [returns, setReturns] = useState<ReturnRecord[]>([]);
  /**
   * null        → modal closed
   * { transaction: Transaction } → opened from Transactions tab (pre-filled)
   * { transaction: null }        → opened from Returns tab (manual TXN ID entry)
   */
  const [returnModalState, setReturnModalState] = useState<{ transaction: Transaction | null } | null>(null);
  /** Transaction pre-loaded into the Exchange tab. Null = show search. */
  const [exchangeTransaction, setExchangeTransaction] = useState<Transaction | null>(null);

  // Restore session from localStorage
  useEffect(() => {
    const token = localStorage.getItem("sessionToken");
    const savedUser = localStorage.getItem("currentUser");
    const savedUserId = localStorage.getItem("currentUserId");
    const savedRole = localStorage.getItem("currentUserRole");

    if (token && savedUser && savedRole) {
      setCurrentUser(savedUser);
      setCurrentUserId(savedUserId || '');
      setCurrentUserRole(savedRole as UserRole);
      setIsAuthenticated(true);

      if (savedRole === 'Admin' || savedRole === 'Viewer')
        setCurrentView(ViewState.Reports);
      else if (savedRole === 'Warehouse Manager')
        setCurrentView(ViewState.Inventory);
      else
        setCurrentView(ViewState.POS);
    }
  }, []);

  useEffect(() => {
    const savedShift = localStorage.getItem("activeShift");
    if (savedShift) {
      const parsed = JSON.parse(savedShift);
      setSessionInfo({
        ...parsed,
        startTime: new Date(parsed.startTime),
        businessDate: new Date(parsed.businessDate)
      });
    }
  }, []);

  useEffect(() => {
    if (isDarkMode) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }, [isDarkMode]);

  const toggleTheme = () => setIsDarkMode(!isDarkMode);


  // ── Audit Log ─────────────────────────────────────────────────────────────
  const addAuditLog = useCallback(async (
    action: string,
    details: string,
    _userOverride?: string
  ) => {
    const userId = currentUserId || localStorage.getItem("currentUserId") || '';

    // Keep a local copy regardless of API success
    const log: AuditLog = {
      id: Math.random().toString(36).substr(2, 9).toUpperCase(),
      timestamp: new Date().toISOString(),
      user: currentUser || _userOverride || 'System',
      action,
      details,
    };
    setAuditLogs(prev => [log, ...prev]);

    if (!userId) return; // can't POST without a userId

    try {
      await fetch(process.env.LOGS_BASE_URL!, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          action: action.slice(0, 32),
          details: details?.slice(0, 255),
        }),
      });
    } catch {
      // audit log failure is non-fatal
    }
  }, [currentUser, currentUserId]);

  // ── Stock Movement Logger ──────────────────────────────────────────────────
  /**
   * Records a stock movement via the backend API.
   * @param productId  UUID from product.id
   * @param stockId    UUID from size.stockId  (NOT the size string!)
   * @param movementType  must be a valid backend enum value
   * @param quantityChange  positive = stock added, negative = stock removed
   * @param updatedStoreStock  new absolute store stock level
   * @param updatedWarehouseStock  new absolute warehouse stock level
   */
  const logStockMovement = useCallback(async (
    productId: string,
    stockId: string,
    movementType: BackendMovementType,
    quantityChange: number,
    updatedStoreStock: number,
    updatedWarehouseStock: number,
    referenceId?: string,
  ) => {
    if (!productId || !stockId) {
      console.warn('logStockMovement: missing productId or stockId — skipped');
      return;
    }

    try {
      const response = await fetch(`${process.env.STOCK_BASE_URL}/record-movement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId,
          stockId,
          movementType,
          quantityChange,
          updatedStoreStock,
          updatedWarehouseStock,
          referenceId,
        }),
      });

      const result = await response.json();

      if (result.OK) {
        setStockMovements(prev => [transformStockMovement(result.DATA), ...prev]);
      } else {
        throw new Error(result.MSG ?? 'Stock movement failed');
      }
    } catch (err) {
      console.error('logStockMovement error:', err);
      throw err;
    }
  }, []);

  // ── Data Fetchers ──────────────────────────────────────────────────────────

  /** Fetches collections, updates state, and returns the transformed list. */
  const fetchCollections = async (): Promise<Collections[]> => {
    try {
      const res = await fetch(process.env.COLLECTION_BASE_URL!, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.OK) {
        const transformed = (data.DATA as any[]).map(transformCollection);
        setCollections(transformed);
        return transformed;
      }
    } catch (err) {
      console.error('fetchCollections error:', err);
    }
    return [];
  };

  /** Fetches products; pass a collections list to resolve names immediately. Returns the list. */
  const fetchProducts = async (collectionsList?: Collections[]): Promise<Product[]> => {
    try {
      const res = await fetch(process.env.PRODUCT_BASE_URL!, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.OK) {
        const cols = collectionsList ?? collections;
        const transformed = (data.DATA as any[]).map(p => transformProduct(p, cols));
        setProducts(transformed);
        return transformed;
      }
    } catch (err) {
      console.error('fetchProducts error:', err);
    }
    return [];
  };

  const fetchCustomers = async (): Promise<Customer[]> => {
    try {
      const res = await fetch(process.env.CUSTOMER_BASE_URL!, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.OK) {
        const transformed = (data.DATA as any[]).map(transformCustomer);
        setCustomers(transformed);
        return transformed;
      }
    } catch (err) {
      console.error('fetchCustomers error:', err);
    }
    return [];
  };

  const fetchStockMovements = async () => {
    try {
      const res = await fetch(process.env.STOCK_BASE_URL!, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.OK) {
        setStockMovements((data.DATA as any[]).map(transformStockMovement));
      }
    } catch (err) {
      console.error('fetchStockMovements error:', err);
    }
  };

  const fetchTransactions = async (
    customersList?: Customer[],
    productsList?: Product[]
  ) => {
    try {
      const url = new URL(process.env.TRANSACTION_BASE_URL as string);
      url.searchParams.append('_t', Date.now().toString());

      const res = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
        },
      });
      const data = await res.json();
      if (data.OK) {
        const custList = customersList ?? customers;
        const prodList = productsList ?? products;
        const rows: any[] = data.DATA;

        // Group rows that share an orderId into one Transaction with multiple items.
        // Rows without an orderId (legacy data) stay as individual transactions.
        const orderMap = new Map<string, any[]>();
        const ungrouped: any[] = [];
        for (const row of rows) {
          if (row.orderId) {
            if (!orderMap.has(row.orderId)) orderMap.set(row.orderId, []);
            orderMap.get(row.orderId)!.push(row);
          } else {
            ungrouped.push(row);
          }
        }

        const grouped: Transaction[] = [];

        // Multi-item orders: merge into one Transaction using the first row as the base
        for (const orderRows of orderMap.values()) {
          // Sort oldest-first so the base row is stable
          orderRows.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
          const base = transformTransaction(orderRows[0], custList, prodList);
          // Collect all items across every row in this order
          const allItems: CartItem[] = orderRows.flatMap(row => {
            const single = transformTransaction(row, custList, prodList);
            return single.items;
          });
          base.items = allItems;
          // isReturned/isExchanged: true only if ALL items share that state
          base.isReturned = orderRows.every(r => r.isReturned);
          base.isExchanged = orderRows.every(r => r.isExchanged);
          grouped.push(base);
        }

        // Legacy single-item rows
        for (const row of ungrouped) {
          grouped.push(transformTransaction(row, custList, prodList));
        }

        // Re-sort newest-first (same order as the raw API response)
        grouped.sort((a, b) => b.date.getTime() - a.date.getTime());
        setTransactions(grouped);
      }
    } catch (err) {
      console.error('fetchTransactions error:', err);
    }
  };

  const fetchShifts = async (usersList?: CashierUser[]) => {
    try {
      
      const res = await fetch(process.env.SHIFT_BASE_URL!, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.OK) {
        const resolvedUsers = usersList ?? users;
        const transformed = (data.DATA as any[]).map((s: any) => {
          const matchedUser = resolvedUsers.find(
            u => (u.id || u._id) === s.cashierId
          );
          const displayName =
            matchedUser?.username || matchedUser?.name || s.cashierId || '';
          return {
            id: s.shiftId,
            shiftId: s.shiftId,
            name: displayName,
            cashierId: s.cashierId,
            cashierName: displayName,
            startTime: s.startTime ? new Date(s.startTime) : null,
            endTime: s.endTime ? new Date(s.endTime) : null,
            shift: s.shiftType,
            shiftType: s.shiftType,
            locationType: s.location === 'Warehouse' ? 'popup' : 'retail',
            location: s.location,
            businessDate: s.startTime ? new Date(s.startTime).toLocaleDateString('en-CA') : '',
            totalSales: s.totalSales || 0,
            cashSales: s.cashSales || 0,
            cardSales: s.cardSales || 0,
            bankTransferSales: s.bankTransferSales || 0,
            transactionCount: s.transactionCount || 0,
          };
        });
        setShiftHistory(transformed);
      }
    } catch (err) {
      console.error('fetchShifts error:', err);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const res = await fetch(process.env.LOGS_BASE_URL!, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.OK) {
        const transformed = (data.DATA as any[]).map((l: any) => ({
          id: l.logId || l.auditLogId || l.id,
          logId: l.logId || l.auditLogId,
          timestamp: l.createdAt,
          user: l.userId || '',
          action: l.action,
          details: l.details || '',
        }));
        setAuditLogs(transformed);
      }
    } catch (err) {
      console.error('fetchAuditLogs error:', err);
    }
  };

  const fetchUsers = async (): Promise<CashierUser[]> => {
    try {
      const res = await fetch(process.env.USER_BASE_URL!, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.OK) {
        const transformed = (data.DATA as any[]).map(transformUser);
        setUsers(transformed);
        return transformed;
      }
    } catch (err) {
      console.error('fetchUsers error:', err);
    }
    return [];
  };

  const fetchReturns = async () => {
    try {
      const res = await fetch(process.env.RETURN_BASE_URL!, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.OK) {
        setReturns(data.DATA as ReturnRecord[]);
      }
    } catch (err) {
      console.error('fetchReturns error:', err);
    }
  };

  // Init: sequential where needed so later fetches can use earlier results
  useEffect(() => {
    const init = async () => {
      // 1. Collections first — needed to resolve product collection names
      const cols = await fetchCollections();
      // 2. Products + customers in parallel (both needed for transactions)
      const [custs] = await Promise.all([
        fetchCustomers(),
        fetchProducts(cols),
      ]);
      // 3. Transactions needs both customers (for display) and products (for item reconstruction)
      //    products state may not have updated yet, so get from a fresh fetch instead
      const prodRes = await fetch(process.env.PRODUCT_BASE_URL!, {
        method: 'GET', headers: { 'Content-Type': 'application/json' }
      });
      const prodData = await prodRes.json();
      const prodList: Product[] = prodData.OK
        ? prodData.DATA.map((p: any) => transformProduct(p, cols))
        : [];

      // Fetch users first so we can resolve cashier names in shifts
      const [usersList] = await Promise.all([
        fetchUsers(),
        fetchTransactions(custs, prodList),
        fetchStockMovements(),
        fetchAuditLogs(),
        fetchReturns(),
      ]);
      // Shifts must run after users so cashierId → name lookup works
      await fetchShifts(usersList);
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Background sync ───────────────────────────────────────────────────────
  // Keeps stock + movement data fresh across ALL machines without a page reload.
  // Uses a ref so the interval always calls the latest version of fetchProducts
  // (which closes over `collections` state — the ref avoids stale-closure issues).
  const syncRef = useRef<{ fp: typeof fetchProducts; fsm: typeof fetchStockMovements }>({
    fp: fetchProducts,
    fsm: fetchStockMovements,
  });
  useEffect(() => {
    syncRef.current = { fp: fetchProducts, fsm: fetchStockMovements };
  });

  useEffect(() => {
    if (!isAuthenticated) return;

    const sync = () => {
      syncRef.current.fp();
      syncRef.current.fsm();
    };

    // Re-sync immediately when the tab becomes visible again (user switches window)
    const onVisibility = () => { if (!document.hidden) sync(); };

    // Poll every 30 s so passive machines stay in sync automatically
    const timer = setInterval(sync, 1_000);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // ── Cart live-sync guard ───────────────────────────────────────────────────
  // Every time products refresh (polling), audit every cart line against the
  // latest data and auto-correct with a cashier-facing toast for each issue.
  //
  // Guards covered:
  //   1. Product pulled from inventory      → remove line, error toast
  //   2. Size/variant removed               → remove line, error toast
  //   3. Stock hits 0 (sold out elsewhere)  → remove line, error toast
  //   4. Stock drops below cart qty         → cap quantity, warning toast
  //   5. Price changed by admin             → update price, warning toast
  //
  // cartRef keeps the effect dep-array as [products] only, so it never fires
  // on cart mutations — preventing the update from retriggering itself.
  const cartRef = useRef<CartItem[]>(cart);
  useEffect(() => { cartRef.current = cart; });

  useEffect(() => {
    if (products.length === 0) return;
    const currentCart = cartRef.current;
    if (currentCart.length === 0) return;

    let needsUpdate = false;
    const updatedCart: CartItem[] = [];

    for (const item of currentCart) {
      // ── Guard 1: product deleted from inventory ──────────────────────────
      const liveProduct = products.find(p => p.id === item.id);
      if (!liveProduct) {
        needsUpdate = true;
        addToast('error', 'Item Removed from Cart',
          `"${item.name}" has been removed from inventory and can no longer be sold.`
        );
        continue; // drop from cart
      }

      // ── Guard 2: size/variant removed ────────────────────────────────────
      const liveSize = liveProduct.stocks.find(s => s.size === item.selectedSize.size);
      if (!liveSize) {
        needsUpdate = true;
        addToast('error', 'Variant Removed from Cart',
          `${item.name} — Size ${item.selectedSize.size} has been discontinued and removed from your cart.`
        );
        continue; // drop from cart
      }

      const liveStock = liveSize.stock;
      let workingItem: CartItem = {
        ...item,
        selectedSize: { ...item.selectedSize, stock: liveStock, warehouseStock: liveSize.warehouseStock },
      };

      // ── Guard 3 & 4: stock exhausted or reduced below cart qty ───────────
      if (item.quantity > liveStock) {
        needsUpdate = true;
        if (liveStock <= 0) {
          addToast('error', 'Item Removed from Cart',
            `${item.name} (Size ${item.selectedSize.size}) sold out at another branch — removed from your cart.`
          );
          continue; // drop from cart
        } else {
          addToast('warning', 'Cart Quantity Reduced',
            `${item.name} (Size ${item.selectedSize.size}) — only ${liveStock} unit(s) remain. Quantity adjusted from ${item.quantity} → ${liveStock}.`
          );
          workingItem = { ...workingItem, quantity: liveStock };
        }
      }

      // ── Guard 5: price changed ────────────────────────────────────────────
      if (liveProduct.price !== item.price) {
        needsUpdate = true;
        addToast('warning', 'Price Updated',
          `${item.name} price changed: ₨ ${item.price.toFixed(0)} → ₨ ${liveProduct.price.toFixed(0)}. Cart total recalculated.`
        );
        workingItem = { ...workingItem, price: liveProduct.price };
      }

      updatedCart.push(workingItem);
    }

    if (needsUpdate) setCart(updatedCart);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  // ── Nav ───────────────────────────────────────────────────────────────────
  const getNavItems = (): NavItem[] => {
    const adminNavItems: NavItem[] = [
      { id: ViewState.Reports, label: 'Analytics', icon: TrendingUp },
      { id: ViewState.Inventory, label: 'Stock In', icon: Archive },
      { id: ViewState.InventoryOut, label: 'Stock OUT', icon: Truck },
      { id: ViewState.StockTrack, label: 'Stock Track', icon: History },
      { id: ViewState.Barcodes, label: 'Barcodes', icon: Barcode },
      { id: ViewState.StockReport, label: 'Stock Reports', icon: FileText },
      { id: ViewState.Customers, label: 'Customers', icon: Users },
      { id: ViewState.Collections, label: 'Collections', icon: Layers },
      { id: ViewState.CashierManagement, label: 'Staff Mgmt', icon: Users },
      { id: ViewState.Transactions, label: 'Store Sales', icon: LayoutDashboard },
      { id: ViewState.Exchange, label: 'Exchange', icon: ArrowLeftRight },
      { id: ViewState.WarehouseTransfer, label: 'To Warehouse', icon: Truck },
      { id: ViewState.AuditLogs, label: 'Audit Logs', icon: ClipboardList },
      { id: ViewState.Backup, label: 'Backup & Data', icon: Database },
    ];
    const cashierNavItems: NavItem[] = [
      { id: ViewState.POS, label: 'Point of Sale', icon: ShoppingCart },
      { id: ViewState.Transactions, label: 'Transactions', icon: LayoutDashboard },
      { id: ViewState.Exchange, label: 'Exchange', icon: ArrowLeftRight },
      { id: ViewState.WarehouseTransfer, label: 'To Warehouse', icon: Truck },
    ];
    const warehouseNavItems: NavItem[] = [
      { id: ViewState.Inventory, label: 'Stock In', icon: Archive },
      { id: ViewState.InventoryOut, label: 'Stock OUT', icon: Truck },
      { id: ViewState.StockTrack, label: 'Stock Track', icon: History },
      { id: ViewState.Barcodes, label: 'Barcodes', icon: Barcode },
      { id: ViewState.StockReport, label: 'Stock Report', icon: FileText },
      { id: ViewState.Collections, label: 'Collections', icon: Layers },
    ];
    const viewerNavItems: NavItem[] = [
      { id: ViewState.Reports, label: 'Analytics', icon: TrendingUp },
      { id: ViewState.StockReport, label: 'Stock Reports', icon: FileText },
      { id: ViewState.StockTrack, label: 'Stock Track', icon: History },
      { id: ViewState.Transactions, label: 'Transactions', icon: LayoutDashboard },
      { id: ViewState.Customers, label: 'Customers', icon: Users },
      { id: ViewState.AuditLogs, label: 'Audit Logs', icon: ClipboardList },
    ];
    switch (currentUserRole) {
      case 'Admin': return adminNavItems;
      case 'Cashier': return cashierNavItems;
      case 'Warehouse Manager': return warehouseNavItems;
      case 'Viewer': return viewerNavItems;
      default: return [];
    }
  };

  // ── Auth ──────────────────────────────────────────────────────────────────
  const handleLogin = (user: CashierUser) => {
    const displayName = user.username || user.name || '';
    const userId = user.id || user._id || '';

    setCurrentUser(displayName);
    setCurrentUserId(userId);
    setCurrentUserRole(user.role);
    setIsAuthenticated(true);

    localStorage.setItem('sessionToken', 'active');
    localStorage.setItem('currentUser', displayName);
    localStorage.setItem('currentUserId', userId);
    localStorage.setItem('currentUserRole', user.role);

    addAuditLog('Login', `User ${displayName} logged in as ${user.role}`);
    addToast('success', 'Access Authorized', `Logged in as ${displayName}`);

    if (user.role === 'Admin' || user.role === 'Viewer')
      setCurrentView(ViewState.Reports);
    else if (user.role === 'Warehouse Manager')
      setCurrentView(ViewState.Inventory);
    else
      setCurrentView(ViewState.POS);
  };

  const handleLogout = async () => {
    if (qz.websocket.isActive()) {
      await qz.websocket.disconnect();
    }

    addAuditLog('Logout', `User ${currentUser} logged out`);
    addToast('info', 'Session Ended', 'Logged out successfully');
    localStorage.clear();

    setTimeout(() => {
      setSessionInfo(null);
      setCurrentUser('');
      setCurrentUserId('');
      setCurrentUserRole(null);
      setIsAuthenticated(false);
      setCart([]);
      setSelectedCustomer(null);
    }, 0);
  };

  // ── Shift ─────────────────────────────────────────────────────────────────
  const handleStartShift = (location: 'retail' | 'popup') => {
    const now = new Date();
    const pakistanDateStr = now.toLocaleDateString('en-CA', { timeZone: 'Asia/Karachi' });
    const { shift } = getShiftDetails(now);

    const shiftData = {
      startTime: now.toISOString(),
      shift,
      businessDate: pakistanDateStr,
      cashierName: currentUser,
      locationType: location,
    };

    setSessionInfo({
      ...shiftData,
      startTime: new Date(shiftData.startTime),
      businessDate: new Date(pakistanDateStr),
    });

    localStorage.setItem('activeShift', JSON.stringify(shiftData));
    addAuditLog('Start Shift', `${shift} shift started at ${location} (PKT)`);
    addToast('success', 'Shift Started', `${shift} shift active for ${pakistanDateStr}`);
  };

  const confirmEndShift = async () => {
    if (!sessionInfo) return;

    const endTime = new Date();
    const sessionTxns = transactions.filter(t => new Date(t.date) >= sessionInfo.startTime);
    const totalSales = sessionTxns.reduce((acc, t) => acc + t.total, 0);

    const shiftPayload = {
      cashierId: currentUserId,
      startTime: sessionInfo.startTime.toISOString(),
      endTime: endTime.toISOString(),
      shiftType: toBackendShiftType(sessionInfo.shift),
      location: toBackendLocation(sessionInfo.locationType),
      totalSales,
      cashSales: sessionTxns
        .filter(t => t.paymentMethod === 'Cash')
        .reduce((acc, t) => acc + t.amountPaid, 0),
      cardSales: sessionTxns
        .filter(t => t.paymentMethod === 'Card')
        .reduce((acc, t) => acc + t.amountPaid, 0),
      bankTransferSales: sessionTxns
        .filter(t => t.paymentMethod === 'Bank Transfer')
        .reduce((acc, t) => acc + t.amountPaid, 0),
      transactionCount: sessionTxns.length,
    };

    try {
      const res = await fetch(process.env.SHIFT_BASE_URL!, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(shiftPayload),
      });

      const data = await res.json();
      if (data.OK) {
        addAuditLog(
          'End Shift',
          `${sessionInfo.shift} (${sessionInfo.locationType}) ended. Sales: Rs ${totalSales}`
        );
        setShowEndShiftModal(false);
        localStorage.removeItem('activeShift');
        handleLogout();
      } else {
        addToast('error', 'Sync Error', data.MSG || 'Failed to save shift to server');
      }
    } catch {
      addToast('error', 'Network Error', 'Check your connection and try again');
    }
  };

  // ── POS Checkout ──────────────────────────────────────────────────────────
  const handlePaymentConfirm = async (
    method: 'Cash' | 'Card' | 'Bank Transfer' | 'N/A',
    referenceNumber?: string,
    overrideDetails?: {
      amountPaid: number; balance: number; isPartial: boolean;
      cashReceived: number; changeAmount: number;
    }
  ) => {
    const details = overrideDetails ?? pendingCheckout;
    if (!details || cart.length === 0 || !sessionInfo) return;

    // Cart totals
    const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const discountedTotal = subtotal * (1 - orderDiscount / 100);

    // Walk-in customer: require a customer to be selected (customerId is needed)
    // If no customer, we can still proceed — the backend column is now nullable.
    const customerId = selectedCustomer?._id ?? null;

    // Determine which POS endpoint to use
    const isStoreSale =
      !sessionInfo.locationType || sessionInfo.locationType === 'retail';

    const posEndpoint = isStoreSale
      ? process.env.POS_BASE_URL
      : `${process.env.POS_BASE_URL}/warehouse`;

    const saleType = isStoreSale ? TransactionType.Sale : TransactionType.PR;

    const posPayload = {
      items: cart.map(item => ({
        productStockId: item.selectedSize.productStockId,
        quantity: item.quantity,
        subtotal: item.price * item.quantity,
      })),
      type: saleType,
      customerId,
      paymentMethod: method,
      cashierName: currentUser,
      shiftType: toBackendShiftType(sessionInfo.shift),
      total: discountedTotal,
      orderDiscount: subtotal * (orderDiscount / 100),
      ...(method === 'Bank Transfer' && referenceNumber
        ? { transferReferenceNumber: referenceNumber }
        : {}),
    };

    try {
      const res = await fetch(posEndpoint!, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(posPayload),
      });

      const result = await res.json();

      if (result.OK) {
        // BUG #4 fix: log stock movement for each sold item so Stock Track "Store Sales" tab populates.
        // All items in the same sale share one referenceId so they appear as one row in Stock Track.
        const saleRefId = crypto.randomUUID();
        for (const item of cart) {
          const newStore = Math.max(0, item.selectedSize.stock - item.quantity);
          await logStockMovement(
            item.id,
            item.selectedSize.stockId!,
            'Sale',
            -item.quantity,   // negative = stock leaving
            newStore,
            item.selectedSize.warehouseStock ?? 0,
            saleRefId,
          );
        }

        // Refresh products from backend to get up-to-date stock
        await fetchProducts();

        // Build a display-friendly transaction from the first returned row
        const firstRow = Array.isArray(result.DATA) ? result.DATA[0] : result.DATA;
        const displayTxn: Transaction = {
          id: firstRow?.transactionId ?? `TXN-${Date.now()}`,
          transactionId: firstRow?.transactionId,
          date: new Date(),
          type: saleType,
          items: cart,
          subtotal,
          total: discountedTotal,
          paymentMethod: method,
          amountPaid: details.amountPaid,
          balance: details.balance,
          isPartial: details.isPartial,
          cashReceived: details.cashReceived,
          changeAmount: details.changeAmount,
          customer: selectedCustomer ?? undefined,
          shift: sessionInfo.shift,
          businessDate: sessionInfo.businessDate.toLocaleDateString('en-CA'),
          cashierName: currentUser,
          cashierRole: currentUserRole ?? undefined,
          orderDiscount,
          isReturned: false,
          isExchanged: false,
        };

        setTransactions(prev => [displayTxn, ...prev]);
        setLastTransaction(displayTxn);
        setCart([]);
        setSelectedCustomer(null);
        setOrderDiscount(0);
        setIsPaymentModalOpen(false);
        setPendingCheckout(null);

        addToast('success', 'Sale Confirmed', `Transaction saved.`);
      } else {
        addToast('error', 'Processing Error', result.MSG);
      }
    } catch (err: any) {
      addToast('error', 'System Error', 'Could not connect to server.');
    }
  };

  // ── Return ────────────────────────────────────────────────────────────────
  const finalizeProcessReturn = async (
    reason: string,
    notes: string | null,
    manualTransactionId?: string
  ) => {
    const transactionId =
      manualTransactionId ??
      returnModalState?.transaction?.transactionId ??
      returnModalState?.transaction?.id;

    if (!transactionId) return;

    try {
      const res = await fetch(`${process.env.TRANSACTION_BASE_URL}/return`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transactionId, reason, notes, cashierName: currentUser }),
      });

      const data = await res.json();

      if (data.OK) {
        // Refresh products so stock reflects the restoration
        await fetchProducts();
        // Refresh returns ledger so the new record appears immediately
        await fetchReturns();

        // Mark transaction as returned in local state (only applies when we have the txn object)
        setTransactions(prev =>
          prev.map(t =>
            (t.transactionId === transactionId || t.id === transactionId)
              ? { ...t, isReturned: true }
              : t
          )
        );

        addAuditLog('Replacement', `Transaction ${transactionId} replaced. Reason: ${reason}`);
        addToast('warning', 'Replacement Processed', 'Stock levels restored.');
        setReturnModalState(null);
      } else {
        addToast('error', 'Replacement Failed', data.MSG);
      }
    } catch {
      addToast('error', 'Network Error', 'Could not process replacement.');
    }
  };

  // ── Cart Helpers ──────────────────────────────────────────────────────────
  const handleCheckoutInit = (details: {
    amountPaid: number; balance: number; isPartial: boolean;
    cashReceived: number; changeAmount: number;
  }) => {
    setPendingCheckout(details);

    const isFreeOrder =
      details.amountPaid === 0 && details.balance === 0 && details.cashReceived === 0;

    if (isFreeOrder) {
      handlePaymentConfirm('N/A', undefined, details);
      return;
    }
    setIsPaymentModalOpen(true);
  };

  const updateQuantity = (productId: string, size: string, delta: number) => {
    setCart(prev =>
      prev.map(item => {
        if (item.id === productId && item.selectedSize.size === size) {
          const newQty = Math.max(0, item.quantity + delta);
          const prod = products.find(p => p.id === productId);
          const sizeObj = prod?.stocks.find(s => s.size === size);
          if (sizeObj && newQty > sizeObj.stock) {
            addToast('error', 'Inventory Limit', 'Cannot exceed available store stock');
            return item;
          }
          return { ...item, quantity: newQty };
        }
        return item;
      }).filter(item => item.quantity > 0)
    );
  };

  const addToCart = (product: Product, selectedSize: ProductSize) => {
    if (selectedSize.stock <= 0) {
      addToast('error', 'Out of Stock', `${product.name} (${selectedSize.size}) is not available`);
      return;
    }
    setCart(prev => {
      const existing = prev.find(
        item => item.id === product.id && item.selectedSize.size === selectedSize.size
      );
      if (existing) {
        if (existing.quantity >= selectedSize.stock) {
          addToast('warning', 'Cart at Inventory Limit', `Only ${selectedSize.stock} unit(s) of ${product.name} (${selectedSize.size}) available — all already in cart`);
          return prev;
        }
        return prev.map(item =>
          item.id === product.id && item.selectedSize.size === selectedSize.size
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      addToast('info', 'Item Added', `${product.name} added to cart`);
      return [...prev, { ...product, selectedSize, quantity: 1 }];
    });
  };

  // ── Warehouse Transfer (Store → Warehouse stock movement) ─────────────────
  const handleWarehouseTransferComplete = async (payload: {
    items: { product: Product; size: ProductSize; quantity: number }[];
    details: { recipient: string };
  }) => {
    const { items } = payload;
    const totalUnits = items.reduce((a, i) => a + i.quantity, 0);

    try {
      // const whTransferRefId = crypto.randomUUID();
      for (const { product, size, quantity } of items) {
        const newStore     = (size.stock          ?? 0) - quantity;
        const newWarehouse = (size.warehouseStock  ?? 0) + quantity;

        if (newStore < 0) {
          addToast('error', 'Transfer Failed',
            `Insufficient store stock for ${product.name} Size ${size.size} (available: ${size.stock ?? 0}).`
          );
          return;
        }

        await logStockMovement(
          product.id,
          size.stockId,
          'Store to Warehouse',
          quantity,
          newStore,
          newWarehouse,
          // whTransferRefId,
        );
      }

      await fetchProducts();
      addAuditLog('Warehouse Transfer', `Store → Warehouse: ${totalUnits} unit(s)`);
      addToast('success', 'Transfer Complete', `${totalUnits} unit(s) moved to warehouse.`);
    } catch (err: any) {
      addToast('error', 'Transfer Failed', err?.message || 'Could not complete warehouse transfer.');
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const renderContent = () => {
    const canPerformReturn = currentUserRole === 'Admin' || currentUserRole === 'Cashier';

    switch (currentView) {
      case ViewState.POS:
        return (
          <div className="flex h-full">
            <div className="flex-1 overflow-hidden relative">
              <ProductGrid
                products={products}
                onAddToCart={addToCart}
                collections={collections}
                cart={cart}
              />
            </div>
            <div className="w-[400px] shrink-0 h-full shadow-2xl z-20">
              <Cart
                items={cart}
                customers={customers}
                selectedCustomer={selectedCustomer}
                orderDiscount={orderDiscount}
                setOrderDiscount={setOrderDiscount}
                onSelectCustomer={setSelectedCustomer}
                onAddCustomer={c => {
                  setCustomers(prev => [...prev, c]);
                  addAuditLog('Customer Created', `Added ${c.name}`);
                  addToast('success', 'CRM Updated', `New profile: ${c.name}`);
                }}
                onUpdateQuantity={updateQuantity}
                onRemoveItem={(pid, sid) =>
                  setCart(prev =>
                    prev.filter(item => !(item.id === pid && item.selectedSize.size === sid))
                  )
                }
                onCheckout={handleCheckoutInit}
                onClearCart={() => { setCart([]); setOrderDiscount(0); }}
              />
            </div>
          </div>
        );

      case ViewState.Inventory:
        return (
          <InventoryList
            products={products}
            onAddProduct={p => {
              setProducts(prev => [p, ...prev]);
              addAuditLog('Product Added', p.id);
              addToast('success', 'Article Created', `${p.name} initialized`);
            }}
            onEditProduct={p => setProducts(prev => prev.map(x => x.id === p.id ? p : x))}
            onDeleteProduct={id => setProducts(prev => prev.filter(x => x.id !== id))}
            lowStockThreshold={lowStockThreshold}
            onUpdateThreshold={setLowStockThreshold}
            collections={collections}
            currentUserRole={currentUserRole}
            onCollectionAdded={newCollection => {
              setCollections(prev => [...prev, newCollection]);
            }}
            onStockUpdated={async (_oldProduct, newProduct) => {
              // Direct article edits are administrative adjustments — they do NOT
              // produce a stock movement record. Only explicit transfer flows
              // (To Warehouse, Warehouse to Store) create movement entries.
              addAuditLog('Stock Updated', `${newProduct.name} (${newProduct.id}) adjusted`);
            }}
          />
        );

      case ViewState.InventoryOut:
        return (
          <InventoryOut
            products={products}
            transactions={transactions}
            onProcessOut={async (type, items, details) => {
              const now = new Date();
              const pakistanBusinessDate = now.toLocaleDateString('en-CA', {
                timeZone: 'Asia/Karachi',
              });

              const txItems: CartItem[] = items
                .map(ui => {
                  const prod = products.find(p => p.id === ui.productId);
                  if (!prod) return null;
                  const size = prod.stocks?.find(s => s.size === ui.size);
                  if (!size) return null;
                  return { ...prod, selectedSize: size, quantity: ui.quantity } as CartItem;
                })
                .filter((i): i is CartItem => i !== null);

              if (txItems.length === 0) return;

              const backendType = toBackendMovementType(type as string);

              // ── Transfer types: use logStockMovement, NOT the POS endpoint ──────
              // The POS endpoint only accepts Sale / FnF (store) or PR / Shopify / Pre-order (warehouse).
              // Warehouse↔Store transfers are pure stock movements with no transaction record.
              const isStoreTransfer      = backendType === 'Warehouse to Store'; // WH → Store
              const isWarehouseTransfer  = backendType === 'Store to Warehouse'; // Store → WH

              if (isStoreTransfer || isWarehouseTransfer) {
                try {
                  // const transferRefId = crypto.randomUUID();
                  for (const item of txItems) {
                    const { selectedSize, quantity } = item;
                    const newStore = isStoreTransfer
                      ? (selectedSize.stock ?? 0) + quantity
                      : (selectedSize.stock ?? 0) - quantity;
                    const newWarehouse = isStoreTransfer
                      ? (selectedSize.warehouseStock ?? 0) - quantity
                      : (selectedSize.warehouseStock ?? 0) + quantity;

                    if (newStore < 0) {
                      addToast('error', 'Transfer Failed',
                        `Insufficient store stock for ${item.name} Size ${selectedSize.size} (available: ${selectedSize.stock ?? 0}).`
                      );
                      return;
                    }
                    if (newWarehouse < 0) {
                      addToast('error', 'Transfer Failed',
                        `Insufficient warehouse stock for ${item.name} Size ${selectedSize.size} (available: ${selectedSize.warehouseStock ?? 0}).`
                      );
                      return;
                    }

                    await logStockMovement(
                      item.id,
                      selectedSize.stockId!,
                      backendType,
                      quantity,
                      newStore,
                      newWarehouse,
                      // transferRefId,
                    );
                  }
                  await fetchProducts();
                  addAuditLog(
                    isStoreTransfer ? 'Store Transfer' : 'Warehouse Transfer',
                    `${backendType}: ${txItems.length} item(s).`
                  );
                  addToast('success', 'Transfer Complete',
                    `${txItems.reduce((a, i) => a + i.quantity, 0)} unit(s) moved to ${isStoreTransfer ? 'store' : 'warehouse'}.`
                  );
                } catch (err: any) {
                  addToast('error', 'Transfer Failed', err?.message || 'Could not complete transfer.');
                }
                return;
              }

              // ── POS endpoint for all other outbound types ────────────────────────
              // Only POS sale deducts from store stock; all other outbound types (FnF, PR, Shopify, Pre-order) deduct from warehouse.
              const storeTypes = new Set(['Sale']);
              const posEndpoint = storeTypes.has(backendType)
                ? process.env.POS_BASE_URL
                : `${process.env.POS_BASE_URL}/warehouse`;

              const subtotal = txItems.reduce((acc, item) => acc + item.price * item.quantity, 0);
              const discountedTotal = subtotal * (1 - (details.discount || 0) / 100);

              const posPayload = {
                items: txItems.map(item => ({
                  productStockId: item.selectedSize.productStockId,
                  quantity: item.quantity,
                  subtotal: item.price * item.quantity,
                })),
                type: backendType,
                customerId: null,
                paymentMethod: 'N/A',
                cashierName: currentUser,
                shiftType: toBackendShiftType(sessionInfo?.shift ?? 'Day'),
                total: discountedTotal,
                // Cap to total: backend rejects orderDiscount > total (e.g. PR = 100% off → total = 0)
                orderDiscount: Math.min(subtotal * ((details.discount || 0) / 100), discountedTotal),
                transferReferenceNumber:
                  (details.recipient || details.orderId)?.slice(0, 24) || undefined,
              };

              try {
                const res = await fetch(posEndpoint!, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(posPayload),
                });
                const result = await res.json();

                if (result.OK) {
                  // Log a stock movement for every item so the Stock Track tabs
                  // (SHOPIFY, PR, FNF, PRE ORDER, STORE SALES) actually populate.
                  // All items in the same transaction share one referenceId so they
                  // collapse into a single row in Stock Track instead of N rows.
                  // Use the human-readable order/recipient ref when available.
                  const txRefId = details.orderId?.trim() || details.recipient?.trim();
                  const isStoreDeduct = storeTypes.has(backendType);
                  for (const item of txItems) {
                    const { selectedSize, quantity } = item;
                    const newStore = isStoreDeduct
                      ? Math.max(0, (selectedSize.stock ?? 0) - quantity)
                      : (selectedSize.stock ?? 0);
                    const newWarehouse = isStoreDeduct
                      ? (selectedSize.warehouseStock ?? 0)
                      : Math.max(0, (selectedSize.warehouseStock ?? 0) - quantity);
                    await logStockMovement(
                      item.id,
                      selectedSize.stockId!,
                      backendType,
                      -quantity,   // negative = stock leaving
                      newStore,
                      newWarehouse,
                      txRefId,
                    );
                  }

                  await fetchProducts();

                  const distTxn: Transaction = {
                    id: `MV-${Math.random().toString(36).substr(2, 5).toUpperCase()}`,
                    date: now,
                    type: type as TransactionType,
                    items: txItems,
                    subtotal,
                    total: discountedTotal,
                    paymentMethod: 'N/A',
                    amountPaid: discountedTotal,
                    balance: 0,
                    isPartial: false,
                    shift: sessionInfo?.shift ?? 'Day',
                    businessDate: pakistanBusinessDate,
                    cashierName: currentUser,
                    cashierRole: currentUserRole ?? undefined,
                    orderDiscount: details.discount,
                  };

                  setTransactions(prev => [distTxn, ...prev]);

                  if (type === TransactionType.PR || type === TransactionType.FnF) {
                    setLastDistribution(distTxn);
                  }

                  addAuditLog(
                    'Inventory Out',
                    `${backendType}: ${items.length} items. Ref: ${details.orderId || details.recipient || '-'}`
                  );
                  addToast('success', 'Logistics Finalized', `${items.length} item(s) processed.`);
                } else {
                  addToast('error', 'Out Error', result.MSG);
                }
              } catch {
                addToast('error', 'Network Error', 'Could not process stock out.');
              }
            }}
          />
        );

      case ViewState.StockTrack:
        return (
          <StockTrackView
            movements={stockMovements}
            products={products}
            userRole={currentUserRole}
            onReturnGroup={async (groupMovements) => {
              for (const movement of groupMovements) {
                const stockId = movement.stockId || movement.size; // size is stockId UUID
                if (!movement.productId || !stockId) continue;

                const currentProduct = products.find(p => p.id === movement.productId);
                const currentSz = currentProduct?.stocks.find(
                  s => s.stockId === stockId || s.size === movement.size
                );
                if (!currentSz) continue;

                const returnQty = Math.abs(movement.quantityChange);
                const newWh = currentSz.warehouseStock + returnQty;

                await logStockMovement(
                  movement.productId,
                  stockId,
                  'Store to Warehouse',   // items returning to warehouse stock
                  returnQty,              // positive = stock being restored
                  currentSz.stock,        // store level unchanged
                  newWh,
                );
              }
              await fetchProducts();
              addAuditLog(
                'Stock Return',
                `${groupMovements.length} item(s) returned to warehouse from stock-out`
              );
              addToast('success', 'Return Processed', 'Items restored to warehouse stock.');
            }}
          />
        );

      case ViewState.Barcodes:
        return (
          <BarcodeDirectory
            products={products}
            onUpdateProduct={async (p) => {
              // Optimistic update so the UI reflects the change immediately
              setProducts(prev => prev.map(x => x.id === p.id ? p : x));
              // Persist barcode(s) to backend via PUT /products/:id
              try {
                await fetch(`${process.env.PRODUCT_BASE_URL}/${p.id}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    stocks: p.stocks.map(s => ({
                      size: s.size,
                      storeStock: s.stock,
                      warehouseStock: s.warehouseStock,
                      barcode: s.barcode ?? null,
                    })),
                  }),
                });
                // Re-fetch so the component always shows the persisted value
                await fetchProducts();
              } catch (err) {
                console.error('Failed to persist barcode:', err);
              }
            }}
            collections={collections}
            userRole={currentUserRole}
            addToast={addToast}
          />
        );

      case ViewState.StockReport:
        return (
          <StockReport
            products={products}
            collections={collections}
            movements={stockMovements}
            userRole={currentUserRole}
          />
        );

      case ViewState.Transactions:
        return (
          <TransactionList
            transactions={transactions}
            onViewReceipt={setLastTransaction}
            currentUser={currentUser}
            onReturn={canPerformReturn ? (txn: Transaction) => setReturnModalState({ transaction: txn }) : undefined}
            onExchange={canPerformReturn ? (txn) => {
              setExchangeTransaction(txn);
              setCurrentView(ViewState.Exchange);
            } : undefined}
            inventory={products}
            addToast={addToast}
            refreshTransactions={() => fetchTransactions()}
            refreshProducts={fetchProducts}
          />
        );

      case ViewState.Customers:
        return (
          <CustomerList
            customers={customers}
            onAddCustomer={c => setCustomers(prev => [...prev, c])}
            onEditCustomer={c =>
              setCustomers(prev => prev.map(x => x._id === c._id ? c : x))
            }
            onDeleteCustomer={id =>
              setCustomers(prev => prev.filter(x => x._id !== id))
            }
            transactions={transactions}
            userRole={currentUserRole}
          />
        );

      case ViewState.Reports:
        return (
          <ReportsDashboard
            transactions={transactions}
            products={products}
            movements={stockMovements}
          />
        );

      case ViewState.CashierManagement:
        return (
          <CashierDashboard
            shiftHistory={shiftHistory}
            users={users}
            onAddUser={u => setUsers(prev => [...prev, u])}
            onEditUser={u => setUsers(prev => prev.map(x => x._id === u._id ? u : x))}
            onDeleteUser={id => setUsers(prev => prev.filter(x => x._id !== id))}
            currentUserRole={currentUserRole}
          />
        );

      case ViewState.AuditLogs:
        return <AuditLogsView logs={auditLogs} userRole={currentUserRole} />;

      case ViewState.Backup:
        return (
          <BackupExportView
            data={{ products, transactions, customers, users, shiftHistory, stockMovements }}
          />
        );

      case ViewState.Collections:
        return (
          <CollectionsView
            collections={collections}
            products={products}
            onEditCollection={c =>
              setCollections(prev => prev.map(x => x.id === c.id ? c : x))
            }
            onAddCollection={c => setCollections(prev => [...prev, c])}
            onDeleteCollection={c =>
              setCollections(prev => prev.filter(x => x.id !== c.id))
            }
          />
        );

      case ViewState.WarehouseTransfer:
        return (
          <WarehouseTransferView
            products={products}
            onComplete={handleWarehouseTransferComplete}
          />
        );

      case ViewState.Exchange:
        return (
          <ExchangeView
            prefillTransaction={exchangeTransaction}
            transactions={transactions}
            inventory={products}
            currentUser={currentUser}
            onClearPrefill={() => setExchangeTransaction(null)}
            onExchangeComplete={async () => {
              // BUG #16 fix: log exchange in audit trail
              addAuditLog('Exchange', `Size exchange completed by ${currentUser}`);
              await Promise.all([fetchTransactions(), fetchProducts()]);
            }}
            addToast={addToast}
          />
        );

      default:
        return null;
    }
  };

  if (!localStorage.getItem('sessionToken')) {
    return <Login onLogin={handleLogin} />;
  }

  if (currentUserRole === 'Cashier' && !sessionInfo) {
    return (
      <StartShift
        username={currentUser}
        onStartShift={handleStartShift}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <>
      <Layout
        currentView={currentView}
        onChangeView={setCurrentView}
        isDarkMode={isDarkMode}
        onToggleTheme={toggleTheme}
        onEndShift={currentUserRole === 'Cashier' ? () => setShowEndShiftModal(true) : undefined}
        onLogout={currentUserRole !== 'Cashier' ? handleLogout : undefined}
        sessionInfo={sessionInfo}
        currentUser={currentUser}
        userRole={currentUserRole}
        navItems={getNavItems()}
      >
        {renderContent()}
      </Layout>

      <AIAssistant products={products} />
      <PaymentModal
        isOpen={isPaymentModalOpen}
        total={pendingCheckout?.amountPaid || 0}
        onClose={() => setIsPaymentModalOpen(false)}
        onConfirm={handlePaymentConfirm}
      />
      <ReceiptModal
        transaction={lastTransaction}
        onClose={() => setLastTransaction(null)}
        addToast={addToast}
      />
      <DistributionReceiptModal
        transaction={lastDistribution}
        onClose={() => setLastDistribution(null)}
        addToast={addToast}
      />
      <ReturnReasonModal
        isOpen={returnModalState !== null}
        transaction={returnModalState?.transaction ?? null}
        onClose={() => setReturnModalState(null)}
        onConfirm={finalizeProcessReturn}
      />
      {sessionInfo && (
        <EndShiftModal
          isOpen={showEndShiftModal}
          onClose={() => setShowEndShiftModal(false)}
          onConfirmEndShift={confirmEndShift}
          sessionInfo={sessionInfo}
          transactions={transactions}
        />
      )}
    </>
  );
};

export default App;
